(() => {
  if (customElements.get('hcc-promo-tote')) return;

  class PromoTote extends HTMLElement {
    connectedCallback() {
      this.config = JSON.parse(this.querySelector('[data-promo-config]').textContent);
      if (!this.config.enabled) return;
      this.controller = new AbortController();
      this.queue = Promise.resolve();
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      this.select = this.querySelector('select');
      this.status = this.querySelector('[data-promo-status]');
      this.addButton = this.querySelector('[data-promo-add]');
      this.picture = this.querySelector('[data-promo-image]');
      this.fallbackImage = this.picture.src;
      this.root = window.Shopify?.routes?.root || '/';
      this.cartPending = false;
      this.watchCartRequests();
      const options = { signal: this.controller.signal };
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        if (!this.busy && this.select.value) this.run(() => this.addTote(Number(this.select.value)));
      }, options);
      this.select.addEventListener('change', () => this.showImage(), options);
      this.querySelectorAll('[data-promo-close]').forEach((button) => {
        button.addEventListener('click', () => this.dismiss(), options);
      });
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        if (!this.busy) this.dismiss();
      }, options);
      document.addEventListener('cart:update', (event) => {
        this.cartPending = false;
        if (event.detail?.sourceId !== 'hcc-promo-tote') this.scheduleSync();
      }, options);
      document.addEventListener('quantity-selector:update', (event) => {
        if (event.target instanceof Element && event.target.closest('cart-items-component')) this.cartPending = true;
      }, options);
      document.addEventListener('cart:error', () => { this.cartPending = false; this.scheduleSync(); }, options);
      document.addEventListener('discount:update', () => this.scheduleSync(), options);
      window.addEventListener('pageshow', () => this.scheduleSync(), options);
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) this.scheduleSync();
      }, options);
      document.addEventListener('click', (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (target?.closest('[data-promo-tote-open]')) {
          event.preventDefault();
          this.run(() => this.openOffer(true));
          return;
        }
        if (target?.closest('[name="checkout"], a[href="/checkout"], a[href$="/checkout"]')) {
          this.guardCheckout(event);
        }
      }, { ...options, capture: true });
      document.addEventListener('submit', (event) => {
        if (event.submitter?.name === 'checkout') this.guardCheckout(event);
      }, { ...options, capture: true });
      this.observer = new MutationObserver(() => this.updateNotices());
      this.observer.observe(document.body, { childList: true, subtree: true });
      this.scheduleSync();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      window.__hccPromoToteCartWatcher?.elements.delete(this);
      clearTimeout(this.timer);
      this.dialog?.close();
    }

    watchCartRequests() {
      const watcher = window.__hccPromoToteCartWatcher;
      if (watcher) {
        watcher.elements.add(this);
        return;
      }

      const elements = new Set([this]);
      const nativeFetch = window.fetch.bind(window);
      const cartMutation = (input, init = {}) => {
        const method = (init.method || input?.method || 'GET').toUpperCase();
        const href = typeof input === 'string' || input instanceof URL ? input : input?.url;
        if (method === 'GET' || !href) return false;
        const path = new URL(href, location.href).pathname;
        return /^\/cart\/(add|change|update|clear)(\.js)?$/.test(path);
      };

      window.__hccPromoToteCartWatcher = { elements };
      window.fetch = async (...args) => {
        const shouldSync = cartMutation(args[0], args[1]);
        const response = await nativeFetch(...args);
        if (shouldSync) {
          setTimeout(() => {
            for (const element of [...elements]) {
              if (element.isConnected) element.scheduleSync();
              else elements.delete(element);
            }
          }, 0);
        }
        return response;
      };
    }

    run(action) {
      this.queue = this.queue.then(async () => {
        if (!this.isConnected) return;
        this.setBusy(true);
        try {
          await action();
        } catch (error) {
          this.status.textContent = error.message || this.config.text.error;
          this.showDialog();
        } finally {
          this.setBusy(false);
        }
      });
      return this.queue;
    }

    scheduleSync() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.run(() => this.sync()), 120);
    }

    setBusy(value) {
      this.busy = value;
      this.addButton.disabled = value;
      this.select.disabled = value;
      this.querySelectorAll('[data-promo-close]').forEach((button) => { button.disabled = value; });
      this.addButton.textContent = value ? this.config.text.adding : this.config.text.add;
      this.form.setAttribute('aria-busy', String(value));
    }

    async request(path, body) {
      const response = await fetch(this.root + path, {
        method: body ? 'POST' : 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      const data = await response.json();
      if (!response.ok || data.errors) throw new Error(this.config.text.error);
      return data;
    }

    isGift(item) {
      return item.properties?._hcc_promo_tote === 'buy-3';
    }

    eligibleQuantity(cart) {
      return cart.items.reduce((sum, item) => {
        const metadata = this.config.lineMetadata.find((product) => Number(product.productId) === Number(item.product_id));
        const fields = [item.title, item.sku, item.product_type, metadata?.title, metadata?.handle, ...(metadata?.tags || [])];
        const service = fields.some((field) => /\badd[\s-]?ons?\b|\bservices?\b/i.test(field || ''));
        const inCollection = metadata?.collections?.some((id) => [473775472868, 474203783396, 475799978212, 475927118052].includes(Number(id)));
        const excluded = !inCollection || this.isGift(item) || Number(item.product_id) === 8866728902884 || item.requires_shipping === false || service;
        return sum + (!excluded && item.original_price > 0 ? item.quantity : 0);
      }, 0);
    }

    freeGift(cart) {
      return cart.items.find((item) => this.isGift(item) && item.quantity === 1 && item.final_line_price === 0);
    }

    storage(value) {
      const key = 'hcc-promo-tote-dismissed:' + (this.cart?.token || 'cart');
      try {
        if (value === undefined) return sessionStorage.getItem(key) === '1';
        if (value) sessionStorage.setItem(key, '1');
        else sessionStorage.removeItem(key);
      } catch { return this.dismissed || false; }
      this.dismissed = value;
    }

    async changeGift(item, quantity) {
      const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((element) => element.dataset.sectionId))].slice(0, 5);
      const cart = await this.request('cart/change.js', {
        id: item.key, quantity, sections, sections_url: location.pathname,
      });
      this.cart = cart;
      this.publish(cart, cart.sections);
      return cart;
    }

    publish(cart, sections) {
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: cart, sourceId: 'hcc-promo-tote', data: { sections, itemCount: cart.item_count } },
      }));
      this.updateNotices();
    }

    async normalize(cart) {
      let kept = false;
      this.cleanupFailed = false;
      const eligible = this.eligibleQuantity(cart) >= 3;
      for (const previous of cart.items.filter((item) => this.isGift(item))) {
        const current = cart.items.find((item) => item.key === previous.key) || cart.items.find((item) => this.isGift(item) && item.variant_id === previous.variant_id);
        if (!current) continue;
        const quantity = eligible && !kept ? 1 : 0;
        if (quantity) kept = true;
        if (current.quantity !== quantity) {
          try { cart = await this.changeGift(current, quantity); }
          catch {
            this.cleanupFailed = true;
            throw new Error(this.config.text.cleanupError);
          }
        }
      }
      for (const item of cart.items.filter((line) => this.isGift(line) && line.final_line_price !== 0)) {
        try { cart = await this.changeGift(item, 0); }
        catch { this.cleanupFailed = true; throw new Error(this.config.text.cleanupError); }
      }
      return cart;
    }

    async sync() {
      await this.refreshChoices();
      const cart = await this.request('cart.js');
      const hadGift = cart.items.some((item) => this.isGift(item));
      this.cart = await this.normalize(cart);
      this.updateNotices();
      if (this.eligibleQuantity(this.cart) < 3) {
        this.storage(false);
        this.declined = false;
        if (this.dialog.open) {
          this.dialog.close();
          this.form.hidden = true;
          this.status.textContent = hadGift ? this.config.text.removed : this.config.text.ineligible;
        }
        return;
      }
      if (this.freeGift(this.cart) && this.dialog.open) this.dialog.close();
      if (!this.freeGift(this.cart) && !this.storage() && !this.dialog.open) await this.openOffer();
    }

    async refreshChoices() {
      const response = await fetch(this.root + '?section_id=' + encodeURIComponent(this.dataset.sectionId), { cache: 'no-store', credentials: 'same-origin' });
      if (!response.ok) throw new Error(this.config.text.error);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const config = html.querySelector('[data-promo-config]');
      if (!config) throw new Error(this.config.text.error);
      this.config = JSON.parse(config.textContent);
    }

    async openOffer(force = false) {
      await this.refreshChoices();
      this.cart = await this.normalize(await this.request('cart.js'));
      if (this.eligibleQuantity(this.cart) < 3) return;
      if (this.freeGift(this.cart)) return;
      if (!force && this.storage()) return;
      if (this.dialog.open) return;
      this.status.textContent = '';
      const variants = this.config.variants.filter((variant) => variant.available);
      this.form.hidden = !this.config.selectionEnabled || !this.config.productId || !variants.length;
      this.querySelector('#hcc-tote-description').textContent = this.form.hidden ? this.config.text.notice : this.config.text.selection;
      this.select.replaceChildren(this.select.options[0]);
      for (const variant of variants) this.select.add(new Option(variant.title, String(variant.id)));
      this.select.value = '';
      if (this.config.selectionEnabled && this.config.productId && !variants.length) {
        this.querySelector('#hcc-tote-description').textContent = this.config.text.unavailable;
      }
      this.showImage();
      this.showDialog();
    }

    showDialog() {
      if (this.isConnected && !this.dialog.open) this.dialog.showModal();
    }

    showImage() {
      const variant = this.config.variants.find((item) => String(item.id) === this.select.value);
      this.picture.src = variant?.image || this.fallbackImage;
      this.picture.alt = variant?.title || '';
    }

    dismiss() {
      this.storage(true);
      this.declined = true;
      this.dialog.close();
      this.updateNotices();
    }

    updateNotices() {
      if (!this.cart) return;
      const eligible = this.eligibleQuantity(this.cart) >= 3;
      const gift = this.freeGift(this.cart);
      for (const notice of document.querySelectorAll('[data-promo-tote-notice]')) {
        if (notice.hidden === eligible) notice.hidden = !eligible;
        const button = notice.querySelector('[data-promo-tote-open]');
        const show = eligible && !gift && this.config.selectionEnabled && this.config.productId;
        if (button && button.hidden === Boolean(show)) button.hidden = !show;
      }
      for (const buttons of document.querySelectorAll('.additional-checkout-buttons')) {
        const hide = Boolean(eligible && !gift && !this.declined && this.config.selectionEnabled);
        if (hide) buttons.dataset.promoToteHidden = 'true';
        if (hide || buttons.dataset.promoToteHidden === 'true') {
          buttons.hidden = hide;
          if (!hide) delete buttons.dataset.promoToteHidden;
        }
      }
    }

    guardCheckout(event) {
      if (this.busy || this.cartPending || this.cleanupFailed) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!this.busy && !this.cartPending) this.run(() => this.sync());
        return;
      }
      if (this.cart && this.eligibleQuantity(this.cart) >= 3 && !this.freeGift(this.cart) && !this.declined) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.run(() => this.openOffer(true));
      }
    }

    async addTote(variantId) {
      this.status.textContent = '';
      await this.refreshChoices();
      this.cart = await this.normalize(await this.request('cart.js'));
      if (this.eligibleQuantity(this.cart) < 3) {
        this.form.hidden = true;
        throw new Error(this.config.text.ineligible);
      }
      if (this.freeGift(this.cart)) { this.dialog.close(); return; }
      await this.refreshChoices();
      if (!this.config.selectionEnabled || !this.config.variants.some((variant) => variant.id === variantId && variant.available)) {
        await this.openOffer(true);
        throw new Error(this.config.text.unavailable);
      }
      for (const gift of this.cart.items.filter((item) => this.isGift(item))) this.cart = await this.changeGift(gift, 0);
      const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((element) => element.dataset.sectionId))].slice(0, 5);
      this.cleanupFailed = true;
      const result = await this.request('cart/add.js', {
        items: [{ id: variantId, quantity: 1, properties: { _hcc_promo_tote: 'buy-3', Promotion: 'Free tote with 3 purchased items' } }],
        sections, sections_url: location.pathname,
      });
      this.cart = await this.normalize(await this.request('cart.js'));
      if (!this.freeGift(this.cart)) {
        for (const gift of this.cart.items.filter((item) => this.isGift(item))) {
          try { this.cart = await this.changeGift(gift, 0); }
          catch { this.cleanupFailed = true; throw new Error(this.config.text.cleanupError); }
        }
        throw new Error(this.config.text.notFree);
      }
      this.publish(this.cart, result.sections);
      this.storage(true);
      this.dialog.close();
    }
  }

  customElements.define('hcc-promo-tote', PromoTote);
})();
